'use client';

import * as React from 'react';

import { cn } from '../../../../lib/utils';
import { cva } from 'class-variance-authority';

// =========================================================================
// Tabs Context
// =========================================================================

export interface TabsContextValue {
  value?: string;
  size?: 'md' | 'lg';
  onValueChange?: (value: string) => void;
  registerTab?: (value: string, element: HTMLElement) => void;
  unregisterTab?: (value: string) => void;
  tabElements?: Map<string, HTMLElement>;
  keepMounted?: boolean;
  isShowIndicatorBar?: boolean;
  isIndicatorAnimate?: boolean;
  rootId: string;
  orientation: 'horizontal' | 'vertical';
  activationMode: 'auto' | 'manual';
  renderHidden: 'hidden' | 'visibility';
  getTabId: (value: string) => string;
  getPanelId: (value: string) => string;
  registerOrder?: (value: string, isDisabled: boolean) => void;
  unregisterOrder?: (value: string) => void;
  tabValuesOrder?: React.RefObject<string[]>;
  tabDisabledMap?: React.RefObject<Map<string, boolean>>;
  registerPanel?: (value: string) => void;
  unregisterPanel?: (value: string) => void;
  panelValues?: Set<string>;
}

const TabsContext = React.createContext<TabsContextValue | null>(null);

const useTabsContext = () => {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs 컴포넌트는 Tabs 내부에서 사용되어야 합니다.');
  }
  return context;
};

// =========================================================================
// Tabs (root)
// =========================================================================

export interface TabsProps extends React.ComponentProps<'div'> {
  /* 기본 탭 컴포넌트 속성 */
  defaultValue?: string;
  /* 현재 선택된 탭의 value (제어된 컴포넌트) */
  value?: string;
  /* 탭 크기 */
  size?: 'md' | 'lg';
  /* 탭 값이 변경될 때 호출되는 함수 */
  onValueChange?: (value: string) => void;
  /* 탭 컨텐츠 렌더링 방식 설정 true: 컴포넌트 마운트 유지 (renderHidden 정책에 따라 hidden 처리), false: 조건부 렌더링 (컴포넌트 언마운트 됨) */
  keepMounted?: boolean;
  /* 탭 인디케이터 바 표시 여부 */
  isShowIndicatorBar?: boolean;
  /* 탭 인디케이터 바 애니메이션 여부 */
  isIndicatorAnimate?: boolean;
  /**
   * 탭 방향. 'horizontal' (default) 또는 'vertical'.
   * - horizontal: 하단 2px 인디케이터, ArrowLeft/Right 키 순회.
   * - vertical: 우측 2px 인디케이터 (LTR-only, RTL 미대응), ArrowUp/Down 키 순회.
   * `aria-orientation` 속성 자동 부여.
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * 키보드 화살표 동작 모드. APG Tabs Pattern.
   * - 'auto' (default): 화살표 = focus 이동 + 즉시 활성화
   * - 'manual': 화살표 = focus 만, Enter/Space 로 활성화
   * scroll-spy 기반 caller (예: IntroduceTabSection) 처럼 onValueChange 가 비싼 사이드이펙트를 동반하면 'manual' 권장.
   */
  activationMode?: 'auto' | 'manual';
  /**
   * keepMounted=true 시 비활성 패널 처리 방식.
   * - 'hidden' (default): HTML `hidden` 속성 부착 → 브라우저가 display:none 적용 + a11y tree 제거
   * - 'visibility': `visibility:hidden + position:absolute` → ResizeObserver/iframe/Remotion 등 dimension 측정이 필요한 caller 의 escape hatch (caller 컨테이너의 `position: relative` 가정 필요)
   */
  renderHidden?: 'hidden' | 'visibility';
}

function Tabs({
  defaultValue,
  value,
  onValueChange,
  className,
  size = 'md',
  keepMounted = false,
  isShowIndicatorBar = true,
  isIndicatorAnimate = true,
  orientation = 'horizontal',
  activationMode = 'auto',
  renderHidden = 'hidden',
  ...props
}: TabsProps) {
  const reactId = React.useId();
  const rootId = `tabs-${reactId.replace(/:/g, '')}`;

  const [internalValue, setInternalValue] = React.useState(defaultValue || '');
  const [tabElements, setTabElements] = React.useState<
    Map<string, HTMLElement>
  >(new Map());
  const [panelValues, setPanelValues] = React.useState<Set<string>>(new Set());
  const tabValuesOrder = React.useRef<string[]>([]);
  const tabDisabledMap = React.useRef<Map<string, boolean>>(new Map());

  const currentValue = value !== undefined ? value : internalValue;

  const handleValueChange = React.useCallback(
    (newValue: string) => {
      if (value === undefined) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [value, onValueChange],
  );

  const registerTab = React.useCallback((v: string, element: HTMLElement) => {
    setTabElements((prev) => new Map(prev).set(v, element));
  }, []);

  const unregisterTab = React.useCallback((v: string) => {
    setTabElements((prev) => {
      const newMap = new Map(prev);
      newMap.delete(v);
      return newMap;
    });
  }, []);

  const registerOrder = React.useCallback((v: string, isDisabled: boolean) => {
    tabDisabledMap.current.set(v, isDisabled);
    if (!tabValuesOrder.current.includes(v)) {
      tabValuesOrder.current = [...tabValuesOrder.current, v];
    }
  }, []);

  const unregisterOrder = React.useCallback((v: string) => {
    tabDisabledMap.current.delete(v);
    tabValuesOrder.current = tabValuesOrder.current.filter((x) => x !== v);
  }, []);

  const registerPanel = React.useCallback((v: string) => {
    setPanelValues((prev) => {
      if (prev.has(v)) return prev;
      const next = new Set(prev);
      next.add(v);
      return next;
    });
  }, []);

  const unregisterPanel = React.useCallback((v: string) => {
    setPanelValues((prev) => {
      if (!prev.has(v)) return prev;
      const next = new Set(prev);
      next.delete(v);
      return next;
    });
  }, []);

  const getTabId = React.useCallback(
    (v: string) => `${rootId}-tab-${v}`,
    [rootId],
  );
  const getPanelId = React.useCallback(
    (v: string) => `${rootId}-panel-${v}`,
    [rootId],
  );

  const contextValue = React.useMemo<TabsContextValue>(
    () => ({
      value: currentValue,
      size,
      onValueChange: handleValueChange,
      registerTab,
      unregisterTab,
      tabElements,
      keepMounted,
      isShowIndicatorBar,
      isIndicatorAnimate,
      rootId,
      orientation,
      activationMode,
      renderHidden,
      getTabId,
      getPanelId,
      registerOrder,
      unregisterOrder,
      tabValuesOrder,
      tabDisabledMap,
      registerPanel,
      unregisterPanel,
      panelValues,
    }),
    [
      currentValue,
      handleValueChange,
      registerTab,
      unregisterTab,
      tabElements,
      size,
      keepMounted,
      isShowIndicatorBar,
      isIndicatorAnimate,
      rootId,
      orientation,
      activationMode,
      renderHidden,
      getTabId,
      getPanelId,
      registerOrder,
      unregisterOrder,
      registerPanel,
      unregisterPanel,
      panelValues,
    ],
  );

  return (
    <TabsContext.Provider value={contextValue}>
      <div
        data-slot="tabs"
        data-size={size}
        data-orientation={orientation}
        className={cn('group w-full', className)}
        {...props}
      />
    </TabsContext.Provider>
  );
}

// =========================================================================
// TabsListContext (TabsList 내부 격리용 — Tabs Context 와 분리)
// =========================================================================
// activationMode='manual' fallback focus 추적용. Tabs Context 에 노출하면
// caller 25곳 전체가 재렌더되므로 TabsList 의 자식 (TabItem) 만 consume
// 하도록 별도 Provider 로 분리한다.
type TabsListContextValue = {
  focusedValue: string | undefined;
  setFocusedValue: (v: string | undefined) => void;
  focusedValueRef: React.MutableRefObject<string | undefined>;
};
const TabsListContext = React.createContext<TabsListContextValue | null>(null);

// =========================================================================
// TabsList
// =========================================================================

export interface TabsListProps extends React.ComponentProps<'div'> {
  /**
   * 탭 그룹의 의미를 키보드/스크린리더 사용자에게 announce.
   * `aria-labelledby` 가 있으면 생략 가능. 둘 중 하나는 명시 권장.
   */
  'aria-label'?: string;
}

function TabsList({
  className,
  onKeyDown: onKeyDownProp,
  onBlur: onBlurProp,
  ...props
}: TabsListProps) {
  const {
    value: selectedValue,
    tabElements,
    isShowIndicatorBar,
    isIndicatorAnimate,
    orientation,
    activationMode,
    onValueChange,
    tabValuesOrder,
    tabDisabledMap,
  } = useTabsContext();
  const listRef = React.useRef<HTMLDivElement>(null);

  // activationMode='manual' 에서 화살표 이동 시 focus 만 옮겨 selectedValue 와
  // 어긋나는 시점이 생긴다 (가드 c). focusedValueRef 로 cleanup 비교, state 로
  // tabIndex fallback 렌더링. focusedValue 는 Tabs Context 비노출.
  const [focusedValue, setFocusedValueState] = React.useState<
    string | undefined
  >(undefined);
  const focusedValueRef = React.useRef<string | undefined>(undefined);
  const setFocusedValue = React.useCallback((v: string | undefined) => {
    focusedValueRef.current = v;
    setFocusedValueState(v);
  }, []);

  const tabsListContextValue = React.useMemo<TabsListContextValue>(
    () => ({ focusedValue, setFocusedValue, focusedValueRef }),
    [focusedValue, setFocusedValue],
  );
  // cross-axis 추상화: size = horizontal 시 width / vertical 시 height,
  // offset = horizontal 시 left / vertical 시 top
  const [indicatorStyle, setIndicatorStyle] = React.useState<{
    size: number;
    offset: number;
    opacity: number;
  }>({
    size: 0,
    offset: 0,
    opacity: 0,
  });

  const updateIndicatorStyle = React.useCallback(() => {
    if (!selectedValue || !tabElements || !listRef.current) {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
      return;
    }

    const selectedElement = tabElements.get(selectedValue);
    if (!selectedElement) {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
      return;
    }

    const listRect = listRef.current.getBoundingClientRect();
    const tabRect = selectedElement.getBoundingClientRect();

    // LTR-only, RTL 미대응 — i18n 확장 시 logical property 전환 (별도 PR) 필요
    if (orientation === 'vertical') {
      setIndicatorStyle({
        size: tabRect.height,
        offset: tabRect.top - listRect.top,
        opacity: 1,
      });
    } else {
      setIndicatorStyle({
        size: tabRect.width,
        offset: tabRect.left - listRect.left,
        opacity: 1,
      });
    }
  }, [selectedValue, tabElements, orientation]);

  React.useLayoutEffect(() => {
    updateIndicatorStyle();
  }, [updateIndicatorStyle]);

  React.useEffect(() => {
    if (!selectedValue || !tabElements || !listRef.current) {
      return;
    }

    const selectedElement = tabElements.get(selectedValue);
    if (!selectedElement) {
      return;
    }

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateIndicatorStyle);
      return () => {
        window.removeEventListener('resize', updateIndicatorStyle);
      };
    }

    const observer = new ResizeObserver(updateIndicatorStyle);
    observer.observe(listRef.current);
    observer.observe(selectedElement);

    return () => {
      observer.disconnect();
    };
  }, [selectedValue, tabElements, updateIndicatorStyle]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDownProp?.(e);
      if (e.defaultPrevented) return;

      const order = tabValuesOrder?.current ?? [];
      if (order.length === 0) return;

      const PREV = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';
      const NEXT = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';

      // pivot: focusedValueRef 가 우선. manual mode 에서 selectedValue 가
      // 갱신되지 않아도 focus 따라 다중 ArrowKey 이동 가능 (APG 부합).
      // ref 는 stable 하므로 deps 추가 불필요.
      const pivot = focusedValueRef.current ?? selectedValue;
      const idx = pivot ? order.indexOf(pivot) : -1;
      if (idx === -1 && e.key !== 'Home' && e.key !== 'End') return;

      // disabled skip 동작 (APG): "Disabled tab is focusable but not activatable" — 단 ArrowKey 는 enabled 만 순회
      const findNextEnabled = (startIdx: number, direction: 1 | -1): number => {
        const len = order.length;
        let cur = startIdx;
        for (let i = 0; i < len; i++) {
          cur = (cur + direction + len) % len;
          const v = order[cur];
          if (v && !tabDisabledMap?.current.get(v)) {
            return cur;
          }
        }
        return startIdx;
      };

      let nextIdx = idx;
      switch (e.key) {
        case PREV:
          nextIdx = findNextEnabled(idx, -1);
          break;
        case NEXT:
          nextIdx = findNextEnabled(idx, 1);
          break;
        case 'Home':
          nextIdx = findNextEnabled(-1, 1);
          break;
        case 'End':
          nextIdx = findNextEnabled(order.length, -1);
          break;
        default:
          return;
      }

      e.preventDefault();
      const nextValue = order[nextIdx];
      if (!nextValue || nextValue === pivot) {
        // focus 만 이동 (활성 변경 없음)
        tabElements?.get(nextValue ?? pivot ?? '')?.focus();
        return;
      }

      tabElements?.get(nextValue)?.focus();
      if (activationMode === 'auto') {
        onValueChange?.(nextValue);
      }
    },
    [
      onKeyDownProp,
      selectedValue,
      tabValuesOrder,
      tabDisabledMap,
      tabElements,
      orientation,
      activationMode,
      onValueChange,
    ],
  );

  // 가드 (d): tablist 외부로 focus 가 빠져나가면 fallback focus 해제.
  // relatedTarget 이 null (cross-origin iframe blur 등) 인 경우 contains(null) 은
  // false 를 반환하므로 null-safe.
  const handleBlur = React.useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      onBlurProp?.(e);
      const next = e.relatedTarget as Node | null;
      if (!e.currentTarget.contains(next)) {
        setFocusedValue(undefined);
      }
    },
    [onBlurProp, setFocusedValue],
  );

  return (
    <TabsListContext.Provider value={tabsListContextValue}>
      <div
        ref={listRef}
        role="tablist"
        aria-orientation={orientation}
        data-slot="tabs-list"
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={cn(
          'relative flex w-full items-center transition-all',
          orientation === 'vertical' && 'flex-col',
          className,
        )}
        {...props}
      >
        {props.children}
        {/* Animated indicator bar — orientation 별 좌표 분기 (LTR-only, RTL 미대응) */}
        {isShowIndicatorBar && (
          <div
            aria-hidden="true"
            className={cn(
              'absolute border-2 border-sys-style-neutral-darker transition-all ease-out',
              orientation === 'vertical'
                ? 'right-0 w-[2px]'
                : 'bottom-0 h-[2px]',
              isIndicatorAnimate ? 'duration-200' : 'duration-0',
            )}
            style={
              orientation === 'vertical'
                ? {
                    height: `${indicatorStyle.size}px`,
                    transform: `translateY(${indicatorStyle.offset}px)`,
                    opacity: indicatorStyle.opacity,
                  }
                : {
                    width: `${indicatorStyle.size}px`,
                    transform: `translateX(${indicatorStyle.offset}px)`,
                    opacity: indicatorStyle.opacity,
                  }
            }
          />
        )}
      </div>
    </TabsListContext.Provider>
  );
}

// =========================================================================
// TabItem
// =========================================================================

const TabItemVariant = cva(
  [
    'flex cursor-pointer items-center justify-center p-sm text-comp-accent-large transition-all',
    'hover:bg-sys-bg-surface-hover',
    'active:bg-sys-bg-surface-press',
    '[&_path]:transition-all [&_path]:fill-sys-cont-neutral-lightest',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-sys-style-primary-default focus-visible:ring-offset-2',
  ],
  {
    variants: {
      size: {
        md: 'px-sm py-xs text-comp-moderate-medium!',
        lg: 'py-sm text-comp-accent-large!',
      },
      isSelected: {
        true: 'text-sys-cont-neutral-default [&_path]:fill-sys-cont-neutral-default',
        false: 'text-sys-cont-neutral-lightest',
      },
      isDisabled: {
        true: 'text-sys-cont-neutral-lightest cursor-not-allowed! [&_path]:fill-sys-cont-neutral-disabled-light! hover:bg-transparent! active:bg-transparent!',
        false: 'text-sys-cont-neutral-default',
      },
    },
    compoundVariants: [
      {
        size: 'md',
        isSelected: true,
        class: 'text-comp-accent-medium!',
      },
    ],
    defaultVariants: {
      size: 'md',
    },
  },
);

export interface TabItemProps extends React.ComponentProps<'div'> {
  value: string;
  isDisabled?: boolean;
}

/**
 * 개별 탭 헤더.
 *
 * 마크업: `<div role="tab" tabIndex>` (HTML5 의 button-in-button 금지로
 * nested interactive 요소 — 예: 닫기 버튼 — 를 caller 가 부착할 수 있도록 div 채택).
 * Keyboard: roving tabindex (선택 0, 비선택 -1) + Enter/Space 활성화 + TabsList 의 Arrow/Home/End 위임.
 * ARIA: role/id/aria-selected/aria-controls 자동. caller 의 명시값이 자동값을 override.
 */
function TabItem({
  value,
  className,
  children,
  isDisabled,
  id: idProp,
  onKeyDown: onKeyDownProp,
  onClick: onClickProp,
  onFocus: onFocusProp,
  ...props
}: TabItemProps) {
  const {
    value: selectedValue,
    size,
    onValueChange,
    registerTab,
    unregisterTab,
    registerOrder,
    unregisterOrder,
    getTabId,
    getPanelId,
    panelValues,
  } = useTabsContext();
  // TabsListContext 는 TabsList 가 Provider — 단독 TabItem 사용 시 null 일 수 있어 ctx? 처리.
  const tabsListCtx = React.useContext(TabsListContext);
  const focusedValue = tabsListCtx?.focusedValue;
  // setFocusedValue 만 별도 추출 — TabsList 내부 useCallback([], []) 으로 stable.
  // tabsListCtx 객체는 focusedValue 변경마다 새 객체이므로 deps 에 넣으면
  // 모든 TabItem 의 handleFocus 콜백이 매 focus 마다 재생성됨.
  const listSetFocused = tabsListCtx?.setFocusedValue;
  const tabRef = React.useRef<HTMLDivElement>(null);
  const isSelected = selectedValue === value;
  const tabId = idProp ?? getTabId(value);
  const panelId = getPanelId(value);
  const hasPanel = panelValues?.has(value) ?? false;
  // 가드 (c, roving): APG roving tabindex 단일 보장.
  // - focusedValue=undefined (평시) → isSelected 기준 (default 동작, caller 무영향)
  // - focusedValue 정의됨 → 오직 focusedValue===value 만 tabIndex=0 (transient
  //   frame 에 selectedValue 와 어긋나도 dual tabIndex=0 회피)
  const isRovingTarget =
    focusedValue !== undefined ? focusedValue === value : isSelected;

  // Tab element 등록은 value 변경 시에만 — isDisabled 토글로 indicator bar 가 깜빡이지 않도록 분리
  // 가드 (b): unmount 시 focusedValue 가 자기 자신이면 reset 하여 dangling
  // focusedValue 회피. effect deps 에 focusedValue 절대 포함 금지 (indicator
  // 깜빡임 회피) — ref 로만 비교한다.
  React.useEffect(() => {
    const element = tabRef.current;
    if (element && registerTab) {
      registerTab(value, element);
    }
    return () => {
      if (tabsListCtx?.focusedValueRef.current === value) {
        tabsListCtx.setFocusedValue(undefined);
      }
      unregisterTab?.(value);
    };
    // 이유: tabsListCtx 자체는 focusedValue 변경마다 새 객체이지만,
    // focusedValueRef 는 component lifetime 동안 stable 한 ref object.
    // stale closure 라도 .current 접근이 안전 (ref 특성).
    // focusedValue 를 deps 에 포함하면 매 focus 마다 effect 재실행 → registerTab 사이클 → indicator 깜빡임.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, registerTab, unregisterTab]);

  // 키보드 순회용 disabled map 등록은 isDisabled 변경 추적
  React.useEffect(() => {
    registerOrder?.(value, !!isDisabled);
    return () => {
      unregisterOrder?.(value);
    };
  }, [value, isDisabled, registerOrder, unregisterOrder]);

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onClickProp?.(e);
      if (e.defaultPrevented) return;
      if (!isDisabled) {
        onValueChange?.(value);
      }
    },
    [onClickProp, isDisabled, onValueChange, value],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDownProp?.(e);
      if (e.defaultPrevented) return;
      // nested interactive (예: 닫기 버튼) 가 자체 Enter/Space 처리 → 자기 자신 focus 일 때만 활성화
      if (e.target !== e.currentTarget) return;
      // Enter/Space 활성화 (manual mode 에서 의미. auto mode 에서도 표준 동작 유지)
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!isDisabled) {
          onValueChange?.(value);
        }
      }
    },
    [onKeyDownProp, isDisabled, onValueChange, value],
  );

  const handleFocus = React.useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      onFocusProp?.(e);
      listSetFocused?.(value);
    },
    [onFocusProp, listSetFocused, value],
  );

  return (
    <div
      ref={tabRef}
      role="tab"
      id={tabId}
      aria-selected={isSelected}
      aria-controls={hasPanel ? panelId : undefined}
      aria-disabled={isDisabled || undefined}
      tabIndex={isRovingTarget ? 0 : -1}
      data-slot="tab-item"
      data-selected={isSelected}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      className={cn(
        TabItemVariant({ size, isSelected, isDisabled }),
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// =========================================================================
// TabsContent
// =========================================================================

export interface TabsContentProps extends React.ComponentProps<'div'> {
  value: string;
}

/**
 * 탭 컨텐츠 영역.
 *
 * keepMounted=false 인 비활성 시 unmount.
 * keepMounted=true 비활성 시:
 *  - renderHidden='hidden' (default): HTML `hidden` 속성 + a11y tree 제거 (브라우저 표준)
 *  - renderHidden='visibility': `visibility:hidden + position:absolute` (ResizeObserver/iframe 호환 escape hatch).
 *    caller 컨테이너의 `position: relative` 가정 필요.
 */
function TabsContent({
  value,
  className,
  children,
  id: idProp,
  'aria-labelledby': labelledByProp,
  style: styleProp,
  ...props
}: TabsContentProps) {
  const {
    value: selectedValue,
    keepMounted,
    renderHidden,
    getTabId,
    getPanelId,
    registerPanel,
    unregisterPanel,
  } = useTabsContext();
  const isSelected = selectedValue === value;
  const panelId = idProp ?? getPanelId(value);
  const labelledBy = labelledByProp ?? getTabId(value);

  // TabItem 의 aria-controls 가 실제 마운트된 panel 만 가리키도록 등록 (axe-core dangling reference 방지)
  // keepMounted=false + 비선택 panel 은 아래 early return 으로 DOM 미존재 → 등록 X
  const isPanelInDom = keepMounted || isSelected;
  React.useEffect(() => {
    if (!isPanelInDom) return;
    registerPanel?.(value);
    return () => {
      unregisterPanel?.(value);
    };
  }, [value, isPanelInDom, registerPanel, unregisterPanel]);

  if (!keepMounted && !isSelected) {
    return null;
  }

  const isHidden = keepMounted && !isSelected;
  const useVisibilityHide = isHidden && renderHidden === 'visibility';
  const useHiddenAttr = isHidden && renderHidden === 'hidden';

  const visibilityStyle: React.CSSProperties | undefined = useVisibilityHide
    ? { visibility: 'hidden', position: 'absolute', pointerEvents: 'none' }
    : undefined;

  return (
    <div
      role="tabpanel"
      id={panelId}
      aria-labelledby={labelledBy}
      aria-hidden={useVisibilityHide ? true : undefined}
      tabIndex={isHidden ? -1 : 0}
      hidden={useHiddenAttr}
      data-slot="tabs-content"
      data-value={value}
      className={cn(
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-sys-style-primary-default focus-visible:ring-offset-2',
        className,
      )}
      style={{
        ...visibilityStyle,
        ...styleProp,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

export { Tabs, TabsList, TabItem, TabsContent };
